import React, { useEffect, useMemo, useRef, useState } from "react";
import { App, Select, Button, Spin, Checkbox } from "antd";
import {
  CloseOutlined,
  FilePdfOutlined,
  RedoOutlined,
  CheckOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import sal from "sal.js";
import dompdf from "dompdf.js";
import download from "downloadjs";

import CustomDocumentHeader from "../../components/CustomDocumentHeader";
import iconFemmeUrl from "../../media/icon_femme_fond.svg";
import homepageUrl from "../../media/homepage.svg";
import { fetchEligibilityJobs } from "../../services/referentielService";
import { computeEligibility } from "../../services/eligibilityService";
import "./EligibiliteVFCScreen.css";

// Met la première lettre en majuscule (libellés d'expositions/tâches).
const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

const splitInTwo = (items) => {
  const mid = Math.ceil(items.length / 2);
  return [items.slice(0, mid), items.slice(mid)];
};

// Attend qu'un élément (identifié par son id) soit présent dans le DOM, en scrutant à chaque
// frame. Sert à monter le document PDF hors-écran à la demande (au téléchargement) : on résout
// dès qu'il est rendu, ou on abandonne au bout d'un plafond de sécurité.
const waitForElement = (id, maxFrames = 60) =>
  new Promise((resolve) => {
    let frames = 0;
    const check = () => {
      const el = document.getElementById(id);
      if (el || frames++ >= maxFrames) resolve(el || null);
      else requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
  });

// Normalisation pour une recherche insensible à la casse et aux accents.
const normalize = (s) =>
  (s || "").toString().toUpperCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

function EligibiliteVFCScreen() {
  const { message } = App.useApp();

  const [jobList, setJobList] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [selectedJobs, setSelectedJobs] = useState([]);

  const [computing, setComputing] = useState(false);
  const [result, setResult] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  // Le document PDF (récapitulatif A4) n'est monté qu'au moment du téléchargement : il est lourd
  // (tableaux + cases à cocher par métier) et, rendu en continu, il provoquait un reflow tardif
  // qui interrompait le défilement vers le résultat (retour intempestif en haut de page).
  const [pdfMounted, setPdfMounted] = useState(false);

  const resultRef = useRef(null);

  useEffect(() => {
    sal();
  }, []);

  // Défilement vers le résultat : déclenché uniquement quand un résultat vient d'être calculé.
  // Le bloc de résultat visible est léger (pas d'images, pas de PDF), sa mise en page est donc
  // stable dès la première frame — un simple scroll après peinture suffit et ne peut plus être
  // interrompu. On repart aussi d'un PDF démonté pour chaque nouveau résultat.
  useEffect(() => {
    if (!result) return;
    setPdfMounted(false);
    const raf = requestAnimationFrame(() => {
      resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(raf);
  }, [result]);

  // Chargement de la liste publique des métiers (filtrage côté client à la saisie).
  useEffect(() => {
    let active = true;
    (async () => {
      setLoadingJobs(true);
      const data = await fetchEligibilityJobs(() => {});
      if (!active) return;
      setJobList(Array.isArray(data) ? data : []);
      setLoadingJobs(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  const selectedIds = useMemo(() => new Set(selectedJobs.map((j) => j.id)), [selectedJobs]);

  const options = useMemo(
    () =>
      jobList
        .filter((j) => !selectedIds.has(j.id))
        .map((j) => ({ value: j.id, label: j.label })),
    [jobList, selectedIds]
  );

  const handleSelectJob = (value, option) => {
    setSelectedJobs((prev) => [...prev, { id: value, label: option.label }]);
    setResult(null);
  };

  const handleRemoveJob = (id) => {
    setSelectedJobs((prev) => prev.filter((j) => j.id !== id));
    setResult(null);
  };

  const handleCompute = async () => {
    if (!selectedJobs.length) {
      message.warning("Ajoutez au moins un métier pour lancer le test.");
      return;
    }
    setComputing(true);
    setResult(null);
    try {
      const res = await computeEligibility(selectedJobs, () => {});
      // Le défilement vers le résultat est géré par l'effet dédié (dépendance sur `result`).
      setResult(res);
    } catch (e) {
      message.error("Une erreur est survenue lors du calcul de l'éligibilité. Veuillez réessayer.");
    } finally {
      setComputing(false);
    }
  };

  const handleReset = () => {
    setSelectedJobs([]);
    setResult(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Génération + téléchargement du PDF avec le même mécanisme que les documents de l'outil
  // (cf. onDownloadDocuments dans UpdateVisite.js) : dompdf rend l'élément #evfc-pdf-content en
  // un blob PDF, que downloadjs télécharge directement (pas de boîte de dialogue d'impression).
  // Le document est rendu en place (hors-écran, à pleine largeur A4) — pas de déplacement DOM,
  // pour ne pas entrer en conflit avec le rendu React.
  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      // Le document PDF n'est rendu qu'à la demande : on le monte, puis on attend qu'il soit
      // présent dans le DOM avant de le capturer avec dompdf.
      setPdfMounted(true);
      const contentEl = await waitForElement("evfc-pdf-content");
      if (!contentEl) {
        message.error("Le téléchargement du PDF a échoué. Veuillez réessayer.");
        return;
      }
      const blob = await dompdf(contentEl, {
        pagination: true,
        compress: true,
        format: "a4",
        // Marges haut/bas réservées sur chaque page (mêmes valeurs que le compte-rendu) : le
        // contenu ne touche pas les bords lors de l'impression, même quand le tableau s'étale
        // sur plusieurs pages, et le pied de page a sa place réservée.
        pageConfig: { header: { height: 30, content: "" }, footer: { height: 91, content: "" } },
        // Pied de page sur chaque page (même rendu que les documents de l'outil, en points).
        onJspdfFinish: (pdf) => {
          const total = pdf.internal.getNumberOfPages();
          const H = pdf.internal.pageSize.getHeight();
          for (let j = 1; j <= total; j++) {
            pdf.setPage(j);
            pdf.setTextColor(150);
            pdf.setFontSize(8);
            pdf.text("Document informatif, non opposable", 56, H - 30);
            pdf.setFontSize(10);
            pdf.text(j + "/" + total, 290, H - 30);
          }
        },
      });
      download(blob, dayjs().format("YYYY-MM-DD_HH-mm-ss") + "_eligibilite_vfc.pdf", "application/pdf");
    } catch (e) {
      message.error("Le téléchargement du PDF a échoué. Veuillez réessayer.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="evfc-page">
      <section className="evfc-hero">
        <div className="evfc-inner" data-sal="slide-up" data-sal-duration="500">
          <span className="evfc-eyebrow">Test gratuit · sans inscription</span>
          <h1 className="evfc-title">Suis-je concerné par la visite de fin de carrière&nbsp;?</h1>
          <p className="evfc-subtitle">
            Indiquez les métiers que vous avez exercés au cours de votre carrière. En quelques
            secondes, découvrez si vous êtes susceptible de bénéficier d'une visite médicale de fin
            de carrière et d'un suivi post-professionnel.
          </p>
          <div className="evfc-badges">
            {["Résultat instantané", "2 clics", "Gratuit", "Anonyme"].map((label) => (
              <span className="evfc-badge" key={label}>
                {label}
              </span>
            ))}
          </div>
        </div>
      </section>

      <div className="evfc-wave-to-green" aria-hidden="true">
        <svg viewBox="0 0 1280 120" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 120 H1280 V60 C1000 0 300 120 0 40 Z" fill="#edf5f2" />
        </svg>
      </div>

      <section className="evfc-body">
        <div className="evfc-inner">
          <div className="evfc-card">
            <p className="evfc-card-step">Étape 1</p>
            <h2 className="evfc-card-title">Vos métiers</h2>
            <p className="evfc-card-help">
              Recherchez et ajoutez chacun des métiers que vous avez exercés, même brièvement.
            </p>

            <div className="evfc-search">
              <Select
                showSearch
                size="large"
                value={null}
                placeholder={
                  <span>
                    <SearchOutlined /> &nbsp;Rechercher un métier (ex.&nbsp;: Soudeur, Désamianteur…)
                  </span>
                }
                loading={loadingJobs}
                options={options}
                onSelect={handleSelectJob}
                filterOption={(input, option) => {
                  const words = normalize(input).split(" ").filter(Boolean);
                  const target = normalize(option?.label);
                  return words.every((w) => target.includes(w));
                }}
                notFoundContent={
                  loadingJobs ? <Spin size="small" /> : "Aucun métier ne correspond à votre recherche."
                }
              />
            </div>

            {selectedJobs.length > 0 ? (
              <div className="evfc-jobs">
                {selectedJobs.map((job) => (
                  <span className="evfc-chip" key={job.id}>
                    {job.label}
                    <span
                      className="evfc-chip-remove"
                      onClick={() => handleRemoveJob(job.id)}
                      role="button"
                      aria-label={`Retirer ${job.label}`}
                    >
                      <CloseOutlined style={{ fontSize: 11 }} />
                    </span>
                  </span>
                ))}
              </div>
            ) : (
              <div className="evfc-jobs-empty">
                Aucun métier ajouté pour le moment. Utilisez la recherche ci-dessus.
              </div>
            )}

            <div className="evfc-actions">
              <Button
                type="primary"
                size="large"
                className="evfc-btn-primary"
                icon={<CheckOutlined />}
                loading={computing}
                disabled={!selectedJobs.length}
                onClick={handleCompute}
              >
                Voir mon résultat
              </Button>
              {selectedJobs.length > 0 && (
                <Button size="large" className="evfc-btn-ghost" icon={<RedoOutlined />} onClick={handleReset}>
                  Recommencer
                </Button>
              )}
            </div>

            {result && (
              <div className="evfc-result" id="evfc-result" ref={resultRef}>
                <div className={`evfc-verdict ${result.eligible ? "evfc-verdict--eligible" : "evfc-verdict--not"}`}>
                    <div className="evfc-verdict-icon">{result.eligible ? "✓" : "ℹ"}</div>
                    {result.eligible ? (
                      <>
                        <h3 className="evfc-verdict-title">
                          Vous êtes potentiellement éligible à une visite de fin de carrière
                        </h3>
                        <p className="evfc-verdict-text">
                          Certaines des expositions professionnelles associées aux métiers
                          sélectionnés peuvent nécessiter une évaluation par votre service de santé au
                          travail avant votre départ de l'entreprise ou votre retraite.
                        </p>
                        <p className="evfc-verdict-text">
                          Si vous pensez avoir été exposé à l'un de ces risques, vous êtes invité à
                          télécharger le récapitulatif suivant et à le présenter à votre service de
                          santé au travail lors du rendez-vous pris par vos soins ou par votre
                          employeur sur demande.
                        </p>
                      </>
                    ) : (
                      <>
                        <h3 className="evfc-verdict-title">Vous ne semblez pas éligible à la visite de fin de carrière</h3>
                        <p className="evfc-verdict-text">
                          Selon nos matrices, aucune des expositions associées aux métiers 
                          sélectionnés n'est connue comme susceptible d'induire des effets à 
                          long terme sur la santé, ni ne nécessite un suivi spécifique au regard 
                          des connaissances scientifiques actuelles. Cet outil est indicatif et 
                          ne remplace pas un avis médical, n'hésitez pas à contactez votre 
                          service de santé au travail si vous souhaitez plus d'information.
                        </p>
                      </>
                    )}
                  </div>

                  {result.eligible && result.jobsWithExposures.length > 0 && (
                    <div className="evfc-detail">
                      <h4 className="evfc-detail-title">Expositions identifiées</h4>
                      <p className="evfc-detail-sub">
                        Pour chaque métier, voici les expositions qui justifient un suivi spécifique.
                      </p>
                      <div className="evfc-expo-list">
                        {result.jobsWithExposures.map((job) => (
                          <div className="evfc-job-row" key={job.id}>
                            <div className="evfc-job-name">{job.label}</div>
                            <div className="evfc-job-tags">
                              {job.exposures.map((expo) => (
                                <span className="evfc-tag" key={expo.id}>{expo.libelle}</span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {result.eligible && (
                  <div className="evfc-result-actions">
                    <Button size="large" className="evfc-btn-ghost" icon={<FilePdfOutlined />} loading={downloadingPdf} onClick={handleDownloadPdf}>
                      Télécharger le récapitulatif (PDF)
                    </Button>
                  </div>
                )}

                <p className="evfc-disclaimer">
                  Ce test est fourni à titre indicatif et ne constitue pas un avis médical. Il
                  s'appuie sur les matrices emplois-expositions de référence. Pour toute question,{" "}
                  <Link to="/contact">contactez-nous</Link>.
                </p>

                {/* Document A4 (récapitulatif façon easyvfc) rendu hors-écran, capturé en place
                    par dompdf au téléchargement (cf. handleDownloadPdf). Monté uniquement à la
                    demande (pdfMounted) pour ne pas alourdir l'affichage ni perturber le
                    défilement vers le résultat. */}
                {result.eligible && pdfMounted && (
                <div className="evfc-pdf-offscreen" aria-hidden="true">
                  <div
                    className="editable-pdfContent"
                    id="evfc-pdf-document"
                    style={{ width: "210mm", minHeight: "280mm", position: "relative", backgroundColor: "white", margin: "auto" }}
                  >
                    <div
                      id="evfc-pdf-content"
                      style={{
                        position: "relative",
                        textAlign: "left",
                        width: "auto",
                        fontSize: "14px",
                        lineHeight: "1.5714285714285714",
                        fontFamily: "Helvetica, Arial, sans-serif",
                        listStyle: "none",
                        padding: "0 20mm",
                      }}
                    >
                      <CustomDocumentHeader style={{ margin: "0 -20mm" }} />

                      <h1 style={{ color: "#233551", fontSize: "24px", marginTop: "18px", marginBottom: "18px" }}>
                        Eligibilité potentielle à la visite de fin de carrière
                      </h1>
                      <p>
                        Certaines des expositions professionnelles peuvent nécessiter une évaluation par votre
                        service de santé au travail avant votre départ de l'entreprise ou votre retraite.
                      </p>
                      <p>
                        Ce document peut être transmis à votre service de santé au travail pour aider à la
                        réalisation de votre <b>visite de fin de carrière.</b>
                      </p>
                      <br />
                      <p>
                        Veuillez cocher les expositions professionnelles et les tâches qui vous semblent pertinentes.
                      </p>

                      {result.jobsWithExposures.map((job) => {
                        const [expoLeft, expoRight] = splitInTwo(job.exposures);
                        const [taskLeft, taskRight] = splitInTwo(job.tasks);
                        const renderExpo = (expo) => (
                          <div key={expo.id} divisiondisable="" style={{ margin: "8px 0" }}>
                            <Checkbox>{capitalize(expo.libelle)}</Checkbox>
                          </div>
                        );
                        const renderTask = (task, i) => (
                          <div key={i} divisiondisable="" style={{ margin: "8px 0" }}>
                            <Checkbox>{capitalize(task)}</Checkbox>
                          </div>
                        );
                        const colStyle = { verticalAlign: "top" };
                        return (
                          <div className="cr-card" style={{ pageBreakInside: "avoid", marginBottom: "10mm" }} key={job.id}>
                            <div className="cr-card-label">{job.label}</div>
                            <div className="cr-card-label-invisible-placeholder">{job.label}</div>
                            {/* border-spacing 0 horizontal : les deux colonnes d'une section se
                                touchent (pas de séparateur central visible — le fond de la carte
                                ne transparaît plus entre les td). Le 2px vertical conserve le
                                liseré entre les lignes bleues et les zones blanches. */}
                            <table className="ant-pdfTable" style={{ borderSpacing: "0 2px" }}>
                              <thead>
                                <tr>
                                  <th className="ant-pdfTh" colSpan={2} divisiondisable="">
                                    {job.exposures.length > 1 ? "Expositions possibles" : "Exposition possible"}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td width="50%" className="ant-pdfTd puce-list" style={colStyle}>
                                    {expoLeft.map(renderExpo)}
                                  </td>
                                  <td width="50%" className="ant-pdfTd puce-list" style={colStyle}>
                                    {expoRight.map(renderExpo)}
                                  </td>
                                </tr>
                                {job.tasks.length > 0 && (
                                  <>
                                    <tr>
                                      <th className="ant-pdfTh" colSpan={2} divisiondisable="">
                                        {job.tasks.length > 1 ? "Tâches associées" : "Tâche associée"}
                                      </th>
                                    </tr>
                                    <tr>
                                      <td width="50%" className="ant-pdfTd puce-list" style={colStyle}>
                                        {taskLeft.map(renderTask)}
                                      </td>
                                      <td width="50%" className="ant-pdfTd puce-list" style={colStyle}>
                                        {taskRight.map(renderTask)}
                                      </td>
                                    </tr>
                                  </>
                                )}
                              </tbody>
                            </table>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="evfc-inner evfc-info">
          <div className="evfc-info-row">
            <div className="evfc-info-media">
              <img src={iconFemmeUrl} alt="" className="evfc-info-svg" />
            </div>
            <div className="evfc-info-text">
              <h2 className="evfc-info-title">Qu'est-ce que la visite de fin de carrière&nbsp;?</h2>
              <p>
                La visite de fin de carrière permet d'assurer une transition vie
                professionnelle-retraite et faciliter, le cas échéant, une surveillance médicale&nbsp;:
              </p>
              <ul>
                <li>
                  En établissant une traçabilité et un état des lieux, à date, de certaines
                  expositions à un ou plusieurs facteurs de risque professionnels auxquelles a été
                  soumis le travailleur.
                </li>
                <li>
                  Suivant les expositions&nbsp;:
                  <ul>
                    <li>En mettant en place une surveillance médicale en lien avec le médecin traitant</li>
                    <li>
                      En informant des démarches à effectuer pour bénéficier d'une surveillance
                      post-professionnelle avec la CPAM
                    </li>
                  </ul>
                </li>
              </ul>
              <p>
                La visite de fin de carrière est entièrement prise en charge par votre employeur et
                sera réalisée par un médecin spécialisé.
              </p>
            </div>
          </div>

          <div className="evfc-info-row evfc-info-row--reverse">
            <div className="evfc-info-media">
              <img src={homepageUrl} alt="" className="evfc-info-svg" />
            </div>
            <div className="evfc-info-text">
              <h2 className="evfc-info-title">Comment fonctionne l'outil d'éligibilité</h2>
              <p>
                Nous interrogeons les matrices emploi-exposition et emploi-tâches de Présanse pour
                déterminer si les métiers que vous avez sélectionnés sont susceptibles d'être associés
                à une ou plusieurs expositions professionnelles potentielles répondant aux critères
                d'éligibilité de la visite de fin de carrière, tels que définis par le décret
                n°2021-1065 du 9 août 2021.
              </p>
              <p>
                Si c'est le cas, vous êtes invité à consulter votre service de santé au travail et à
                leur fournir le récapitulatif généré au format PDF, que vous pouvez enregistrer ou
                imprimer.
              </p>
              <p>
                Ce document justifie votre demande de visite par les métiers que vous avez déclarés
                avoir exercés, par les expositions potentielles que vous avez pu rencontrer et par une
                liste de tâches exposantes que vous avez peut-être réalisées.
              </p>
            </div>
          </div>
        </div>

        <div className="evfc-pro-cta">
          <Link to="/notre-solution">
            <Button size="large" className="evfc-pro-btn">
              Professionnel de santé au travail&nbsp;?
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}

export default EligibiliteVFCScreen;
